import { QueryClient } from "@tanstack/react-query";

import { PartDto } from "#modules/parts/infra/fetchPart.api";
import { getPartQueryData, setPartQueryData } from "#modules/parts/infra/part.query";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import {
  getQuoteRequestLineQueryData,
  setQuoteRequestLineQueryData,
} from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { queryKeys } from "#shared/queryKeys/queryKeys";
import {
  ExtendedPart,
  JobStatusSchema,
  PartTypeType,
  shouldAnalysePartAfterTypeUpdate,
} from "shared";

export type UpdatePartTypeCacheSnapshot = {
  quoteRequestLineId: string;
  partId: string;
  previousQuoteRequestLine: QuoteRequestLineDto | undefined;
  previousPart: PartDto | undefined;
};

export const applyOptimisticUpdatePartType = async (
  queryClient: QueryClient,
  {
    partId,
    quoteRequestLineId,
    newPartType,
  }: {
    partId: string;
    quoteRequestLineId: string;
    newPartType: PartTypeType;
  },
): Promise<UpdatePartTypeCacheSnapshot> => {
  const willAnalyzePart = shouldAnalysePartAfterTypeUpdate(newPartType);

  await Promise.all([
    queryClient.cancelQueries({
      queryKey: queryKeys.quoteRequestLines.detail(quoteRequestLineId),
    }),
    queryClient.cancelQueries({
      queryKey: queryKeys.parts.detail(partId),
    }),
  ]);

  const previousQuoteRequestLine = getQuoteRequestLineQueryData(queryClient, quoteRequestLineId);
  const previousPart = getPartQueryData(queryClient, partId);

  if (willAnalyzePart) {
    setPartQueryData(queryClient, partId, (old) => {
      if (!old) {
        return old;
      }

      return {
        ...old,
        type: newPartType,
        analyzeStatus: JobStatusSchema.enum.STANDBY,
        featuresStatus: JobStatusSchema.enum.STANDBY,
      };
    });
  }

  setQuoteRequestLineQueryData(queryClient, quoteRequestLineId, (old) => {
    if (!old) {
      return old;
    }

    return {
      ...old,
      part: {
        ...old.part,
        type: newPartType,
        ...(willAnalyzePart
          ? {
              analyzeStatus: JobStatusSchema.enum.STANDBY,
              featuresStatus: JobStatusSchema.enum.STANDBY,
            }
          : {}),
      },
      predictionStatus: JobStatusSchema.enum.STANDBY,
    };
  });

  return {
    quoteRequestLineId,
    partId,
    previousQuoteRequestLine,
    previousPart,
  };
};

export const applyUpdatePartTypeSuccess = (
  queryClient: QueryClient,
  {
    partId,
    quoteRequestLineId,
    part,
    shouldAnalyzePart,
    shouldPredictAfterAnalyze,
  }: {
    partId: string;
    quoteRequestLineId: string;
    part: ExtendedPart;
    shouldAnalyzePart: boolean;
    shouldPredictAfterAnalyze: boolean;
  },
) => {
  if (shouldAnalyzePart) {
    setPartQueryData(queryClient, part.id, (old) => {
      if (!old) {
        return old;
      }

      return {
        ...old,
        ...part,
      };
    });

    setQuoteRequestLineQueryData(queryClient, quoteRequestLineId, (old) => {
      if (!old) {
        return old;
      }

      return {
        ...old,
        part: { ...old.part, ...part },
        predictionStatus: shouldPredictAfterAnalyze ? JobStatusSchema.enum.STANDBY : null,
      };
    });

    return;
  }

  void queryClient.invalidateQueries({ queryKey: queryKeys.parts.detail(partId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.parts.json(partId) });

  if (shouldPredictAfterAnalyze) {
    setQuoteRequestLineQueryData(queryClient, quoteRequestLineId, (old) => {
      if (!old) {
        return old;
      }

      return {
        ...old,
        part: { ...old.part, ...part },
        predictionStatus: JobStatusSchema.enum.STANDBY,
      };
    });
  }

  void queryClient.invalidateQueries({
    queryKey: queryKeys.quoteRequestLines.detail(quoteRequestLineId),
  });
};

export const rollbackUpdatePartType = (
  queryClient: QueryClient,
  snapshot: UpdatePartTypeCacheSnapshot,
) => {
  setQuoteRequestLineQueryData(
    queryClient,
    snapshot.quoteRequestLineId,
    snapshot.previousQuoteRequestLine,
  );
  setPartQueryData(queryClient, snapshot.partId, snapshot.previousPart);
};
